import * as Yup from "yup";

export const informationSchema = Yup.object().shape({
  formName: Yup.string()
    .trim()
    .required("Form name is required")
    .max(100, "Form name must be at most 100 characters"),
  formStartDate: Yup.date()
    .nullable()
    .required("Start date is required")
    .typeError("Invalid start date"),
  formEndDate: Yup.date()
    .nullable()
    .typeError("Invalid end date")
    .when("alwaysAvailable", {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) =>
        schema
          .required("End date is required")
          .min(
            Yup.ref("formStartDate"),
            "End date must be after the start date"
          ),
    }),
});

export const membershipPricingSchema = Yup.object().shape({
  membershipPricing: Yup.array().of(
    Yup.object().shape({
      amount: Yup.number()
        .nullable()
        .typeError("Amount must be a number")
        .max(1000000, "Amount cannot exceed 1,000,000")
        .when("openAmount", {
          is: false,
          then: (schema) => schema.required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),

      installmentDetailsTransaction: Yup.object().shape({
        numberOfInstallment: Yup.string()
          .nullable()
          .test(
            "required-if-installment",
            "Number of installments is required",
            function (value) {
              const parent = this.options.from[1]?.value;
              if (parent?.isInstallmentTransaction) {
                return !!value;
              }
              return true;
            }
          ),
      }),
      chargeDate: Yup.date().nullable().required("Charge date is required"),
      dueDate: Yup.date().nullable().required("Due date is required"),
      tax_deductible: Yup.string().required("Tax deductible is required"),
      ledger: Yup.object().shape({
        id: Yup.string().required("Ledger is required"),
        name: Yup.string().required("Ledger is required"),
      }),
      addAmount: Yup.number()
        .nullable()
        .typeError("Additional amount must be a number"),
    })
  ),
});

export const dynamicPricingSchema = Yup.object().shape({
  ledgersForCalculation: Yup.array()
    .min(1, "At least one ledger for calculation is required"),
  ledgerForCalculation: Yup.object().shape({
    dateRange: Yup.array()
      .of(Yup.date().nullable())
      .test(
        "both-dates-required",
        "Both start and end dates are required",
        function (value) {
          if (!value || value.length !== 2) return false;
          const [start, end] = value;
          return !!start && !!end;
        }
      ),
    fallbackAmount: Yup.number()
      .typeError("Fallback Amount must be a number")
      .required("Fallback Amount is required"),
  }),
  membershipPricing: Yup.array().of(
    Yup.object().shape({
      installmentDetailsTransaction: Yup.object().shape({
        numberOfInstallment: Yup.string()
          .nullable()
          .test(
            "required-if-installment",
            "Number of installments is required",
            function (value) {
              const parent = this.options.from[1]?.value;
              if (parent?.isInstallmentTransaction) {
                return !!value;
              }
              return true;
            }
          ),
      }),
      ledger: Yup.object().shape({
        id: Yup.string().required("Ledger is required"),
        name: Yup.string().required("Ledger is required"),
      }),
      chargeDate: Yup.date().nullable().required("Charge date is required"),
      dueDate: Yup.date().nullable().required("Due date is required"),
      tax_deductible: Yup.string().required("Tax deductible is required"),
      amount: Yup.number()
        .nullable()
        .typeError("Amount must be a number")
        .max(1000000, "Amount cannot exceed 1,000,000")
        .when("static", {
          is: true,
          then: (schema) => schema.required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
      addAmount: Yup.number()
        .nullable()
        .typeError("Last Year Amount must be a number")
        .max(1000000, "Amount cannot exceed 1,000,000")
        .when("static", {
          is: (val) => !val,
          then: (schema) => schema.required("Last Year Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
    })
  ),
});

export const finalFormSchema = Yup.object().shape({
  addOns: Yup.array().of(
    Yup.object().shape({
      amount: Yup.number()
        .nullable()
        .typeError("Amount must be a number")
        .max(1000000, "Amount cannot exceed 1,000,000")
        .when("openAmount", {
          is: false,
          then: (schema) => schema.required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),

      installmentDetailsTransaction: Yup.object().shape({
        numberOfInstallment: Yup.string()
          .nullable()
          .test(
            "required-if-installment",
            "Number of installments is required",
            function (value) {
              const parent = this.options.from[1]?.value;
              if (parent?.isInstallmentTransaction) {
                return !!value;
              }
              return true;
            }
          ),
      }),
      chargeDate: Yup.date().nullable().required("Charge date is required"),
      dueDate: Yup.date().nullable().required("Due date is required"),
      tax_deductible: Yup.string().required("Tax deductible is required"),
      ledger: Yup.object().shape({
        id: Yup.string().required("Ledger is required"),
        name: Yup.string().required("Ledger name is required"),
      }),
      addAmount: Yup.number()
        .nullable()
        .typeError("Additional amount must be a number"),
    })
  ),
});

export const stepFields = {
  0: ["formName", "formStartDate", "formEndDate"],
  1: [
    "isInstallmentTransaction",
    "amount",
    "installmentDetailsTransaction.numberOfInstallment",
    "installmentDetailsTransaction.lastInstallmentDate",
    "chargeDate",
    "dueDate",
    "tax_deductible",
    "ledger.id",
    "ledger.name",
    "addAmount",
  ],
  2: ["ledger", "dateRange", "fallbackAmount"],
};

export function touchStepFields(formikRef, stepIndex) {
  const values = formikRef?.current?.values || {};

  if (stepIndex === 0) {
    // Simple flat fields
    stepFields[0].forEach((field) =>
      formikRef.current.setFieldTouched(field, true, true)
    );
  }

  if (stepIndex === 1) {
    stepFields[2].forEach((field) =>
      formikRef.current.setFieldTouched(
        `ledgerForCalculation.${field}`,
        true,
        true
      )
    );

    // Array fields in membershipPricing
    (values.membershipPricing || []).forEach((_, index) => {
      stepFields[1].forEach((field) =>
        formikRef.current.setFieldTouched(
          `membershipPricing[${index}].${field}`,
          true,
          true
        )
      );
    });
    (values.addOns || []).forEach((_, index) => {
      stepFields[1].forEach((field) =>
        formikRef.current.setFieldTouched(
          `addOns[${index}].${field}`,
          false,
          false
        )
      );
    });
  }
}

export const omitKeys = (obj, keysToRemove) => {
  const newObj = { ...obj };
  keysToRemove.forEach((key) => delete newObj[key]);
  return newObj;
};
